import React, { useState, useEffect } from 'react';
import { getSectionForToday } from '../utils/WeeklySchedule';

const NavigationPills = ({ currentView, onSectionClick, onClaveAgustinianaClick, allArticles = {} }) => {
  const [todayArticles, setTodayArticles] = useState({
    'Vocaciones': null,
    'Santos Agustinos': null,
    'Te interesa': null,
    'ClaveAgustiniana': null // Para las subsecciones de Clave agustiniana
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Detectar artículos del día al cargar o cuando cambien los artículos
  useEffect(() => {
    const checkTodayArticles = () => {
      const hoy = new Date();
      const mesHoy = hoy.getMonth(); // 0-11
      const diaHoy = hoy.getDate(); // 1-31
      const añoHoy = hoy.getFullYear(); // Año actual

      // Secciones normales a verificar
      const sectionsToCheck = ['Vocaciones', 'Te interesa', 'Santos Agustinos'];
      const newTodayArticles = {};

      sectionsToCheck.forEach(section => {
        const articles = allArticles[section] || [];
        
        // Buscar artículo que coincida con la fecha de hoy (día, mes y año)
        const articleToday = articles.find(articulo => {
          if (!articulo.date) return false;
          const fechaArticulo = new Date(articulo.date);
          return fechaArticulo.getFullYear() === añoHoy && 
                 fechaArticulo.getMonth() === mesHoy && 
                 fechaArticulo.getDate() === diaHoy;
        });

        newTodayArticles[section] = articleToday || null;
        
        if (articleToday) {
          console.log(`✨ ${section}: Artículo del día encontrado - ${articleToday.title}`);
        }
      });

      // Verificar subsecciones de Clave agustiniana
      const todaySections = getSectionForToday();
      const claveAgustinianaSections = Array.isArray(todaySections) ? todaySections : [todaySections];
      
      let claveAgustinianaArticle = null;
      let claveAgustinianaSectionName = '';
      
      for (const section of claveAgustinianaSections) {
        const articles = allArticles[section] || [];
        const articleToday = articles.find(articulo => {
          if (!articulo.date) return false;
          const fechaArticulo = new Date(articulo.date);
          return fechaArticulo.getFullYear() === añoHoy && 
                 fechaArticulo.getMonth() === mesHoy && 
                 fechaArticulo.getDate() === diaHoy;
        });
        
        if (articleToday) {
          claveAgustinianaArticle = articleToday;
          claveAgustinianaSectionName = section;
          console.log(`✨ Clave agustiniana (${section}): Artículo del día encontrado - ${articleToday.title}`);
          break; // Solo necesitamos encontrar uno
        }
      }
      
      newTodayArticles['ClaveAgustiniana'] = claveAgustinianaArticle 
        ? { article: claveAgustinianaArticle, sectionName: claveAgustinianaSectionName }
        : null;

      setTodayArticles(newTodayArticles);
    };

    if (Object.keys(allArticles).length > 0) {
      checkTodayArticles();
    }
  }, [allArticles]);

  // Función para mostrar toast
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000); // 5 segundos para leer
  };

  // Handler mejorado para clicks en secciones con contenido del día
  const handleSectionClick = (section, sectionKey, options = {}) => {
    const todayArticle = todayArticles[sectionKey];
    const { toastPrefix = '🎉 Nuevo', ...navOptions } = options;

    if (todayArticle) {
      // Mostrar toast primero
      showToastMessage(`${toastPrefix}: ${todayArticle.title}`);
      // Esperar más tiempo antes de navegar para que se lea el toast
      setTimeout(() => {
        onSectionClick(section, navOptions);
      }, 1200); // 1.2 segundos para leer
    } else {
      // Sin artículo del día, navegar inmediatamente
      onSectionClick(section, navOptions);
    }
  };

  // Handler especial para Clave agustiniana
  const handleClaveAgustinianaClick = () => {
    const claveAgustinianaData = todayArticles['ClaveAgustiniana'];
    
    if (claveAgustinianaData && claveAgustinianaData.article) {
      // Mostrar toast con información de la subsección
      showToastMessage(`🎉 Nuevo en ${claveAgustinianaData.sectionName}: ${claveAgustinianaData.article.title}`);
      // Esperar antes de navegar
      setTimeout(() => {
        onClaveAgustinianaClick();
      }, 1200); // 1.2 segundos para leer
    } else {
      // Sin artículo del día, navegar inmediatamente
      onClaveAgustinianaClick();
    }
  };
  
  const navigationItems = [
    {
      id: 'clave-agustiniana',
      label: '🔑 Clave agustiniana',
      sectionKey: 'ClaveAgustiniana',
      action: () => {
        console.log('🔑 Abriendo Clave agustiniana');
        handleClaveAgustinianaClick();
      }
    },
    {
      id: 'vocaciones',
      label: '✨ Vocaciones',
      section: 'Vocaciones',
      sectionKey: 'Vocaciones',
      action: () => {
        console.log('📘 Navegando a sección Vocaciones');
        handleSectionClick('Vocaciones', 'Vocaciones');
      }
    },
    {
      id: 'te-interesa',
      label: '🌍 Te interesa',
      section: 'Te interesa',
      sectionKey: 'Te interesa',
      action: () => {
        console.log('📘 Navegando a sección Te interesa');
        handleSectionClick('Te interesa', 'Te interesa');
      }
    },
    {
      id: 'santos-agustinos',
      label: '👼 Santos agustinos',
      section: 'Santos Agustinos',
      sectionKey: 'Santos Agustinos',
      action: () => {
        // Entra en el mes en curso en vez de en el listado completo: la sección
        // ya tiene filtros por mes y son cientos de santos.
        const mesActual = new Date().getMonth();
        console.log(`👼 Navegando a Santos agustinos, mes ${mesActual}`);
        handleSectionClick('Santos Agustinos', 'Santos Agustinos', {
          filter: `mes-${mesActual}`,
          toastPrefix: '📿 Hoy celebramos'
        });
      }
    }
  ];

  return (
    <div className="px-1 md:px-5 py-2 pb-4 md:py-5 bg-white w-full max-w-full overflow-hidden navigation-pills-container">
      {/* Toast de notificación */}
      {showToast && (
        <div
          className="
            fixed left-1/2 transform -translate-x-1/2
            bg-white text-gray-800
            px-6 py-4
            rounded-2xl shadow-2xl z-50
            w-[94%] sm:w-[520px] md:w-[620px] max-w-[98%]
            text-center transition-all duration-300
            break-words
          "
          style={{
            top: 'max(env(safe-area-inset-top) + 80px, 100px)',
            border: '3px solid #e62c19',
            animation: 'slideDown 0.4s ease-out',
            boxShadow: '0 10px 40px rgba(230, 44, 25, 0.3)'
          }}
        >
          <div className="text-base font-bold text-gray-900 leading-snug">
            {toastMessage}
          </div>
        </div>
      )}

      {/* NUEVO: Grid 2x2 para móvil, flex horizontal para desktop */}
      <div 
        className="grid grid-cols-2 gap-2 md:flex md:gap-3"
      >
        {navigationItems.map((item) => (
          <button
            key={item.id}
            onClick={item.action}
            className="modern-button hover:transform hover:-translate-y-0.5 transition-all duration-200 relative"
            style={{
              background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)',
              fontSize: '14px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              borderRadius: '10px',
              boxShadow: 'none' // Sin sombra en estado normal, solo en hover
            }}
          >
            <span>{item.label}</span>
            
            {/* Punto verde si hay contenido del día - NO mostrar en Clave Agustiniana */}
            {item.sectionKey && item.sectionKey !== 'ClaveAgustiniana' && todayArticles[item.sectionKey] && (
              <div 
                className="absolute w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"
                style={{ 
                  top: '4px',
                  right: '6px',
                  boxShadow: '0 0 10px rgba(34, 197, 94, 0.8), 0 0 20px rgba(34, 197, 94, 0.4)'
                }}
              />
            )}
          </button>
        ))}
      </div>

      {/* CÓDIGO ORIGINAL COMENTADO - Modo deslizante horizontal
      <div 
        className="flex gap-2 md:gap-3 overflow-x-auto custom-scrollbar pb-2 navigation-pills-scroll"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        {navigationItems.map((item) => (
          <button
            key={item.id}
            onClick={item.action}
            className="modern-button flex-shrink-0 hover:transform hover:-translate-y-0.5 transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)',
              fontSize: '14px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              boxShadow: 'none'
            }}
          >
            <span>{item.label}</span>
          </button>
        ))}
      </div>
      */}
    </div>
  );
};

export default NavigationPills;